<script lang="ts" setup>
import { useI18n } from "vue-i18n";

import OmniSelect from "@/components/selects/OmniSelect.vue";
import OmniTextField from "@/components/omni/OmniTextField.vue";
import OmniCheckbox from "@/components/omni/OmniCheckbox.vue";
import { useValidation } from "@/composables/useValidation";
import { OrderExpenseType } from "@/constants/order";
import { preventMinusKey, preventMinusPaste } from "@/utils/input";

import type { CreateOrderExpense } from "@/api/orders/dto/order.dto";

const TYPE_OPTIONS = Object.values(OrderExpenseType);

const { t } = useI18n();
const { requiredValidator } = useValidation();

const expense = defineModel<CreateOrderExpense>({ required: true });

const typeOptions = TYPE_OPTIONS.map((type) => {
  return { id: type, name: t(`order-expenses.${type}`) };
});

const typeRules = [(value: unknown) => requiredValidator(value) === true || t("order-expenses.type-required")];

const rateRules = [(value: unknown) => requiredValidator(value) === true || t("order-expenses.rate-required")];

function updateExpense(data: Partial<CreateOrderExpense>) {
  expense.value = { ...expense.value, ...data };
}

function updateType(type: OrderExpenseType | null) {
  updateExpense({ type });
}

function updateRate(value: string) {
  updateExpense({ rate: value === "" ? null : Math.abs(Number(value)) });
}

function updateNote(value: unknown) {
  updateExpense({ note: typeof value === "string" && value ? value : null });
}

function updateAffectsDispatcherProfit(value: unknown) {
  updateExpense({ affectsDispatcherProfit: Boolean(value) });
}
</script>

<template>
  <div class="d-flex flex-column row-gap-4">
    <div class="d-flex align-start col-gap-3">
      <omni-select
        :items="typeOptions"
        :label="t('general.type')"
        :model-value="expense.type"
        :rules="typeRules"
        class="flex-grow-1"
        data-qa="order-expense-type"
        required
        @update:model-value="updateType"
      />

      <v-text-field
        :label="t('general.rate')"
        :model-value="expense.rate ?? ''"
        :rules="rateRules"
        autocomplete="off"
        class="required order-expense-form__rate"
        color="primary"
        data-qa="order-expense-rate"
        density="compact"
        hide-details="auto"
        min="0"
        type="number"
        variant="outlined"
        @keydown="preventMinusKey"
        @paste="preventMinusPaste"
        @update:model-value="updateRate"
      >
        <template #append-inner>
          <span class="font-size-16 text-grey-darken-1">$</span>
        </template>
      </v-text-field>
    </div>

    <omni-text-field
      :label="t('payments.note')"
      :model-value="expense.note"
      data-qa="order-expense-note"
      @update:model-value="updateNote"
    />

    <omni-checkbox
      :label="t('payments.affects-dispatcher-profit')"
      :model-value="expense.affectsDispatcherProfit"
      data-qa="order-expense-affects-dispatcher-profit"
      @update:model-value="updateAffectsDispatcherProfit"
    />
  </div>
</template>

<style lang="scss" scoped>
.order-expense-form__rate {
  flex: 0 0 200px;
}
</style>
